import {
  Container,
  Dropdown,
  DropdownOption,
  FeatureCheckbox,
} from '@/components/chariot-ui';
import { Button } from '@/components/ui/button';
import { getHours, getMinutes, parse } from 'date-fns';
import { InfoIcon } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { ToggleSwitch } from '@/components/legacy-ui/ToggleSwitch';
import { useScanSchedule } from '@/hooks/useScanSchedule';
import { WeekDay, WeekDayOptions } from '@/types';
import { cn } from '@/utils/classname';
import {
  convertDayToUTC,
  convertToUTC,
  getCurrentTimeZone,
} from '@/utils/date.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

export const ScanSchedule = ({
  customerEmail,
  onClose,
}: {
  customerEmail?: string;
  onClose?: () => void;
}) => {
  const scanTimeOptions = useMemo(() => {
    const options = [];

    [...Array(12).keys()].forEach(i => {
      const hour = i <= 9 ? `0${i}` : `${i}`;
      options.push({ label: `${hour}:00 AM`, value: `${hour}:00` });
    });

    options.push({ label: '12:00 PM', value: '12:00' });

    [...Array(11).keys()].forEach(i => {
      const hour = i < 9 ? `0${i + 1}` : `${i + 1}`;
      options.push({ label: `${hour}:00 PM`, value: `${13 + i}:00` });
    });

    return options;
  }, []);
  const { offset, tz } = getCurrentTimeZone();
  const {
    currentScanSchedule,
    isUpdatingScanSchedule,
    updateScanScheduleAsync,
    deleteScanSchedule,
  } = useScanSchedule({ email: customerEmail });
  const timezoneOptions = [
    {
      label: '(GMT+00:00) UTC',
      value: 'UTC',
    },
    {
      label: `(${offset}) ${tz}`,
      value: tz,
    },
  ];

  const [scanDays, setScanDays] = useState<WeekDay[]>(
    currentScanSchedule?.days || []
  );
  const [start, setStart] = useState<DropdownOption | undefined>(
    scanTimeOptions.find(option => option.value === currentScanSchedule.start)
  );
  const [end, setEnd] = useState<DropdownOption | undefined>(
    scanTimeOptions.find(option => option.value === currentScanSchedule.end)
  );
  const [searchStart, setSearchStart] = useState<string>('');
  const [searchEnd, setSearchEnd] = useState<string>('');
  const [savedTimezone, setSavedTimezone] = useStorage<DropdownOption>(
    { key: StorageKey.SCAN_SCHEDULE_TIMEZONE },
    timezoneOptions[0] // Default timezone is UTC
  );
  const [timezone, setTimezone] = useState<DropdownOption>(savedTimezone);
  const [manualBypass, setManualBypass] = useState<boolean>(
    currentScanSchedule?.manualBypass ?? false
  );

  // Sync state with API data when it arrives
  useEffect(() => {
    if (currentScanSchedule) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setScanDays(currentScanSchedule.days || []);

      // The hook already converts the schedule times to HH:mm format
      const startOption = scanTimeOptions.find(
        option => option.value === currentScanSchedule.start
      );
      const endOption = scanTimeOptions.find(
        option => option.value === currentScanSchedule.end
      );

      if (startOption) setStart(startOption);
      if (endOption) setEnd(endOption);
      setManualBypass(currentScanSchedule.manualBypass ?? false);
    }
  }, [currentScanSchedule, scanTimeOptions]);

  function getTimeOptions(search: string, type: 'start' | 'end') {
    const options = scanTimeOptions.filter(option =>
      search ? option.label.includes(search) : true
    );

    if (isUpdatingScanSchedule) {
      return options.map(currentOption => ({
        ...currentOption,
        disabled: true,
      }));
    }

    if (type === 'start') {
      return options.map(option => ({
        ...option,
        disabled: option.value === start?.value,
      }));
    }
    if (!start) {
      return options.map(option => ({ ...option, disabled: false }));
    }

    // Parse start time
    const startTime = parse(start.value as string, 'HH:mm', new Date());

    // Convert time to minutes since the start of the day
    const toMinutes = (time: Date) => getHours(time) * 60 + getMinutes(time);
    const startMinutes = toMinutes(startTime);
    const minGap = 360; // 6 hours in minutes
    const fullDay = 1440; // 24 hours in minutes

    return options.map(option => {
      const optionTime = parse(option.value, 'HH:mm', new Date());
      const optionMinutes = toMinutes(optionTime);

      const isStartDisabled = option.value === start.value;

      // Disable any time within 6 hours after start time
      let isWithinRestrictedRange = false;

      if (startMinutes + minGap < fullDay) {
        // Normal case: Start time and valid range are within the same day
        isWithinRestrictedRange =
          optionMinutes > startMinutes && optionMinutes < startMinutes + minGap;
      } else {
        // Wrap-around case: Start time is late at night, and end time might wrap into the next day
        const cutoffTime = (startMinutes + minGap) % fullDay;
        isWithinRestrictedRange =
          optionMinutes > startMinutes || optionMinutes < cutoffTime;
      }

      return {
        ...option,
        disabled: isStartDisabled || isWithinRestrictedRange,
      };
    });
  }

  useEffect(() => {
    if (!start && !end && scanDays.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStart({ label: '12:00 AM', value: '00:00' });
      setEnd({ label: '11:00 PM', value: '23:00' });
    }
  }, [start, end, scanDays]);

  return (
    <div className="flex flex-col" data-testid="scan-schedule-form">
      <h1 className="text-2xl font-bold">Scan Schedule</h1>
      <p>
        Select a default scan schedule that will be{' '}
        <span className="font-bold italic">applied to all assets</span>. This
        determines how often Chariot scans your assets.
      </p>
      <Container
        className={cn(
          'bg-chariot-background-primary my-4 w-fit px-6 py-4',
          customerEmail && 'w-full'
        )}
      >
        <h2 className="text-md pb-1 font-bold">Days to Scan Assets</h2>
        <div className="flex gap-2 pb-4">
          {WeekDayOptions.map(({ label, value }) => (
            <FeatureCheckbox
              disabled={isUpdatingScanSchedule}
              id={label}
              className={cn('w-[108px]', customerEmail && 'w-full flex-1')}
              key={label}
              title={label}
              checked={scanDays.includes(value)}
              onChange={(checked: boolean) => {
                setScanDays(prev => {
                  if (checked) {
                    return [...prev, value];
                  }
                  return prev.filter(d => d !== value);
                });
              }}
              data-testid={`scan-schedule-day-checkbox-${value}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <h2 className="text-md pb-1 font-bold">Choose a Time</h2>
          <Tooltip title="Requires a minimum scan window of 6 hours">
            <InfoIcon className="text-chariot-text-secondary size-4" />
          </Tooltip>
        </div>
        <div className="flex gap-10">
          <div className="flex items-center gap-4">
            <Dropdown
              options={getTimeOptions(searchStart, 'start')}
              size="xs"
              selected={start ? [start] : []}
              onSelect={(option: DropdownOption) => {
                setStart(option);
                setEnd(undefined);
              }}
              searchValue={searchStart}
              placeholder="Select Start Time"
              onSearchChange={setSearchStart}
              listClassName="max-h-[500px] overflow-y-auto"
              className="w-full"
              data-testid="scan-schedule-start-time-select"
            />
            <p className="font-bold">to</p>
            <Dropdown
              options={getTimeOptions(searchEnd, 'end')}
              size="xs"
              selected={end ? [end] : []}
              onSelect={setEnd}
              placeholder="Select End Time"
              searchValue={searchEnd}
              onSearchChange={setSearchEnd}
              listClassName="max-h-[500px] overflow-y-auto"
              className="w-full"
              data-testid="scan-schedule-end-time-select"
            />
          </div>
          <div className="flex items-center gap-2">
            <h2 className="text-md font-bold">Timezone</h2>
            <Dropdown
              options={timezoneOptions}
              size="xs"
              selected={timezone ? [timezone] : []}
              onSelect={setTimezone}
              listClassName="max-h-[500px] overflow-y-auto"
              className="w-full"
              data-testid="scan-schedule-timezone-select"
            />
          </div>
        </div>
        {!customerEmail && (
          <>
            <div className="border-chariot-stroke my-4 border-t" />
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-md font-bold">
                  Manual scans bypass schedule
                </h2>
                <p className="text-chariot-text-tertiary text-xs">
                  When enabled, manually triggered scans will run immediately
                  regardless of the scan schedule.
                </p>
              </div>
              <ToggleSwitch
                checked={manualBypass}
                onChange={setManualBypass}
                disabled={isUpdatingScanSchedule}
                ariaLabel="Toggle manual scan bypass"
                data-testid="scan-schedule-manual-bypass-toggle"
              />
            </div>
          </>
        )}
      </Container>

      <div className={cn('flex w-fit gap-2', customerEmail && 'ml-auto')}>
        {customerEmail && (
          <Button
            variant="outline"
            onClick={async () => {
              onClose?.();
            }}
            data-testid="scan-schedule-cancel-button"
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={async () => {
            if (start?.value && end?.value && timezone && scanDays.length > 0) {
              setSavedTimezone(timezone);
              if (timezone.value !== 'UTC') {
                const { utcTime: utcStartTime, diff: diffStart } = convertToUTC(
                  start.value as string,
                  timezone.value as string
                );
                const { utcTime: utcEndTime } = convertToUTC(
                  end.value as string,
                  timezone.value as string
                );
                // convert days to utc days
                const days = scanDays.map(day => {
                  return convertDayToUTC(day, diffStart);
                });
                await updateScanScheduleAsync({
                  days,
                  start: utcStartTime,
                  end: utcEndTime,
                  manualBypass,
                });
              } else {
                await updateScanScheduleAsync({
                  days: scanDays,
                  start: start.value as string,
                  end: end.value as string,
                  manualBypass,
                });
              }
              onClose?.();
            }
          }}
          variant="default"
          className="w-fit"
          disabled={!start || !end || !timezone || scanDays.length === 0}
          data-testid="scan-schedule-save-button"
        >
          Save
        </Button>
        {!customerEmail && (
          <Button
            variant="outline"
            onClick={async () => {
              await deleteScanSchedule();
              setScanDays([]);
              setStart(undefined);
              setEnd(undefined);
              setTimezone(timezoneOptions[0]);
            }}
            data-testid="scan-schedule-reset-button"
          >
            Reset to Default
          </Button>
        )}
      </div>
    </div>
  );
};
